import { BE_FORM_DATE_FORMAT, FormInputMoney, FormSelect } from "@farzoom/common-ui-components";
import { Col, Row } from "antd";
import moment, { Moment } from "moment";
import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { Section } from "./section";
import { BgFormType } from "@services/guarantees/types";
import { useBgFormContext } from "@app/guranteeFormWrapper";
import { useStyles } from "@app/guranteeFormWrapper/styles";
import { GUARANTEE_OPTIONS_AGENT } from "@app/guranteeFormWrapper/constants";
import { FormInputDateTime } from "@app/common-ui-components/FormInputDateTime";

const validateStartDate = (current: Moment) => {
  const yesterday = moment().add(-1, "day");
  return current && current < yesterday;
};

const validateEndDate = (current: Moment, startDate: string | null) => {
  if (!startDate) return false;
  return current && current < moment(startDate, BE_FORM_DATE_FORMAT);
};

export const GuaranteeInfo = () => {
  const classes = useStyles();
  const { control, setValue, setError, clearErrors } = useFormContext<BgFormType>();
  const { formDisabled, prefix, canEditPurchase, isStartForm } = useBgFormContext();

  const currentPrefix = (prefix ? `${prefix}.guarantee` : "guarantee") as "guarantee";
  const lotsPrefix = prefix ? `${prefix}.purchase.lots` : "purchase.lots";

  const lots = useWatch({
    control,
    name: lotsPrefix as "purchase.lots",
  });

  const guaranteeAmount = useMemo(() => {
    if (!lots) return 0;

    return (lots || []).reduce((total, lot) => {
      return (
        total +
        (lot.beneficiaries?.reduce((acc, b) => acc + (b.bgAmount ?? 0), 0) ?? 0)
      );
    }, 0);
  }, [lots]);

  useEffect(() => {
    setValue(`${currentPrefix}.amount` as "guarantee.amount", guaranteeAmount);
  }, [guaranteeAmount]);

  const startDate = useWatch({ control, name: `${currentPrefix}.startDate` });
  const endDate = useWatch({ control, name: `${currentPrefix}.endDate` });

  useEffect(() => {
    if (
      endDate &&
      startDate &&
      moment(endDate, BE_FORM_DATE_FORMAT) < moment(startDate, BE_FORM_DATE_FORMAT)
    ) {
      setError(`${currentPrefix}.endDate`, {
        type: "custom",
        message: "Дата окончания не может быть раньше даты начала",
      });
    } else {
      clearErrors(`${currentPrefix}.endDate`);
    }
  }, [startDate, endDate, currentPrefix, setError, clearErrors]);

  return (
    <Section title="Сведения о банковской гарантии">
      <FormSelect
        name={`${currentPrefix}.type`}
        className={classes.input}
        label="Вид банковской гарантии"
        setValue={setValue}
        control={control}
        defaultValue={null}
        options={GUARANTEE_OPTIONS_AGENT}
        disabled={formDisabled || !canEditPurchase}
        required
      />
      <Row gutter={16}>
        <Col span={6}>
          <FormInputDateTime
            name={`${currentPrefix}.startDate`}
            control={control}
            setValue={setValue}
            label="Дата начала гарантии"
            disabled={formDisabled}
            disabledDate={validateStartDate}
            required
          />
        </Col>
        <Col span={6}>
          <FormInputDateTime
            name={`${currentPrefix}.endDate`}
            control={control}
            setValue={setValue}
            label="Дата окончания"
            disabled={formDisabled}
            disabledDate={(current) => validateEndDate(current, startDate)}
            required
          />
        </Col>
        {!isStartForm && (
          <Col span={12}>
            <FormInputMoney
              name={`${currentPrefix}.amount`}
              className={classes.input}
              control={control}
              setValue={setValue}
              label="Сумма гарантии"
              disabled
              maxLength={25}
            />
          </Col>
        )}
      </Row>
    </Section>
  );
};
